import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { IFitbitSleepSession } from "../../../../@types/Fitbit";
import {
  MeasurementTypes,
  SelectMeasurementPayloadType,
} from "../../../../@types/Measurements";
import { Notification } from "../../../../@types/Notification";
import {
  colapsedSectionsPropertyNames,
  ICollapsedSection,
  IPatientInfo,
} from "../../../../@types/Patient";
import { IPatientTreatment } from "../../../../@types/Treatments";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { HorizontalDots } from "../../../../assets/icons/HorizontalDots";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { Toggle } from "../../../../components/Toggle";
import { useMeasurementTypeConfig } from "../../../../hooks/queries/measurement-configs";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { userService } from "../../../../services/userService";
import { NebulizerChartRow } from "../../../../utils/chartUtils";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { ChartsContainer } from "../../PatientPage.styles";
import { AddMeasurementModal } from "../AddMeasurementModal";
import { FitbitSection } from "../MeasurementModal/components/FitbitSection";
import { FitbitSleepSection } from "../MeasurementModal/components/FitbitSleepSection/FitbitSleepSection";
import { FitbitSleepSelectedSession } from "../MeasurementModal/components/FitbitSleepSelectedSession/FitbitSleepSelectedSession";
import { MeasurementSection } from "../MeasurementModal/components/MeasurementSection";
import { ModalSkeleton } from "../MeasurementModal/components/ModalSkeleton";
import { SpirometrySection } from "../MeasurementModal/components/SpirometrySection";
import { StepsSection } from "../MeasurementModal/components/StepsSection";
import { TreatmentSection } from "../MeasurementModal/components/TreatmentSection";
import {
  AddMeasurementButton,
  DotsContainer,
  NewDataContainer,
  OptionsContainer,
  PinnedSectionTitle,
  PinnedTile,
  PinnedToogleContainer,
  TitleContainer,
} from "./PinnedSection.styles";

type PinnedSectionProps = {
  patient: IPatientInfo;
  collapsedSection: ICollapsedSection;
  updateColapsedSections: (section: colapsedSectionsPropertyNames) => void;
  measurementType: MeasurementTypes;
};

export function PinnedSection({
  patient,
  collapsedSection,
  updateColapsedSections,
  measurementType,
}: PinnedSectionProps) {
  const { t } = useTranslation();

  const { isMobile } = useIsMobile();
  const [searchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { portalPreferences, changePortalPreferences } = useUserPreferences();
  const { config, isFetching } = useMeasurementTypeConfig({
    measurementType,
  });
  const { showToast } = useToast();

  const [
    selectedSpirometryMeasurementValue,
    setSelectedSpirometryMeasurementValue,
  ] = useState<SelectMeasurementPayloadType | null>(null);
  const [hideSpirometrySection, setHideSpirometrySection] = useState(false);

  const [isPinned, setIsPinned] = useState(true);
  const [areOptionsOpen, setAreOptionsOpen] = useState(false);
  const [showNewData, setShowNewData] = useState<boolean>(false);
  const [isTreatmentOpen, setIsTreatmentOpen] = useState(false);
  const [treatment, setTreatment] = useState<
    IPatientTreatment | NebulizerChartRow
  >();
  const [selectedSleepSession, setSelectedSleepSession] = useState<
    IFitbitSleepSession | undefined
  >(undefined);

  const isSteps = measurementType === "daily_steps";
  const isFitbitActivity = measurementType === "fitbit_activity";
  const isFitbitSleep = measurementType === "fitbit_sleep";

  const optionsWrapperRef = useRef<any>(null);
  const optionsButtonRef = useRef<any>(null);

  useEffect(() => {
    const spirometryTestId = searchParams.get("spirometryTestId");
    if (spirometryTestId) {
      setSelectedSpirometryMeasurementValue({
        unformattedTime: "",
        time: "",
        fvc: 0,
        spirometryTestIds: [parseInt(spirometryTestId, 10)],
      });
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        areOptionsOpen &&
        optionsWrapperRef.current &&
        !optionsWrapperRef.current.contains(event.target) &&
        !optionsButtonRef.current.contains(event.target)
      ) {
        setAreOptionsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [areOptionsOpen]);

  const handlePinnedGraphChange = async (isPinnedGraph: boolean) => {
    setIsPinned(isPinnedGraph);

    setTimeout(() => {
      setAreOptionsOpen(false);
    }, 200);

    await userService.updatePinnedMeasurementType();

    const portalPrefenrecesToChange = { ...portalPreferences };
    portalPrefenrecesToChange.pinnedMeasurementType = undefined;
    changePortalPreferences(portalPrefenrecesToChange);
  };

  const handleCollapseSection = () => {
    updateColapsedSections("pinnedSection");
    if (collapsedSection?.value === true) {
      setShowNewData(false);
    }
  };

  const checkNewData = (mostRecentDate: dayjs.Dayjs) => {
    if (showNewData === false) {
      const collapsedDate = dayjs(collapsedSection?.collapsedDate);
      if (mostRecentDate > collapsedDate) {
        setShowNewData(true);
      }
    }
  };

  const handleOnModalClose = (notification?: Notification) => {
    if (notification !== undefined) {
      showToast(notification.message, notification.type);

      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
    closeModal();
  };

  const handleAddMeasurement = () => {
    mixpanelActions.track(`User action: Add Measurement Button`);
    openModal(
      <AddMeasurementModal
        section={config?.category ?? "measurement"}
        patientId={patient?.id.toString()}
        onClose={handleOnModalClose}
        measurementType={measurementType}
      />,
      {
        width: isMobile ? "98vw" : "620px",
        height: isMobile ? undefined : "auto",
        borderRadius: isMobile ? "8px" : undefined,
        showCloseButton: true,
      }
    );
  };

  const handleOnTreatmentClick = (
    treatment: IPatientTreatment | NebulizerChartRow
  ) => {
    setTreatment(treatment);
    setIsTreatmentOpen(true);
  };

  const memoMeasurementSection = useMemo(() => {
    return (
      <MeasurementSection
        isPinnedGraph
        cardTitle={`${config?.title} ${
          config?.unit ? `(${config?.unit})` : ""
        }`}
        key={`${config?.title}_${config?.type}_${patient.id}`}
        minValue={config?.min}
        maxValue={config?.max}
        measurementType={measurementType}
        unit={config?.unit}
        patientId={patient.id.toString()}
        patient={patient}
        onSelectChartValue={(selectedChartValue) => {
          setSelectedSpirometryMeasurementValue(selectedChartValue);
        }}
        onSelectedTableView={(showMeasurementsTable) => {
          setHideSpirometrySection(showMeasurementsTable);
          setTreatment(undefined);
          setIsTreatmentOpen(false);
        }}
        checkNewData={checkNewData}
        onTreatmentClick={handleOnTreatmentClick}
        closeTreatmentDetails={() => {
          setTreatment(undefined);
          setIsTreatmentOpen(false);
        }}
      />
    );
  }, [config]);

  // Fitbit sleep/activity and steps are not line-chart measurements, so the
  // generic MeasurementSection can't render them — it would show nothing and
  // the pinned graph would appear to "disappear". Branch to the dedicated
  // sections, mirroring MeasurementModal.
  const fitbitCardTitle = `${config?.title ?? ""} ${
    config?.unit ? `(${config?.unit})` : ""
  }`;

  let pinnedMeasurementContent: JSX.Element;
  if (isSteps) {
    pinnedMeasurementContent = (
      <StepsSection
        isPinnedGraph
        cardTitle={fitbitCardTitle}
        patientId={patient.id.toString()}
        unit={config?.unit}
        onSelectedTableView={(showTable) => setHideSpirometrySection(showTable)}
      />
    );
  } else if (isFitbitActivity) {
    pinnedMeasurementContent = (
      <FitbitSection
        isPinnedGraph
        cardTitle={fitbitCardTitle}
        patientId={patient.id.toString()}
        onSelectedTableView={(showTable) => setHideSpirometrySection(showTable)}
      />
    );
  } else if (isFitbitSleep) {
    pinnedMeasurementContent = (
      <FitbitSleepSection
        isPinnedGraph
        cardTitle={fitbitCardTitle}
        patientId={patient.id.toString()}
        onSelectedTableView={(showTable) => setHideSpirometrySection(showTable)}
        selectedSession={selectedSleepSession}
        onSessionChange={setSelectedSleepSession}
      />
    );
  } else {
    pinnedMeasurementContent = memoMeasurementSection;
  }

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PinnedSectionTitle>
            {t("patientPage_sections_pinned")}
          </PinnedSectionTitle>
          {showNewData && (
            <NewDataContainer>
              {t("patientPage_sections_newData")}
            </NewDataContainer>
          )}
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection?.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}

      <ChartsContainer
        style={{
          height: collapsedSection?.value ? "0" : "",
          overflow: collapsedSection?.value ? "hidden" : "",
        }}
      >
        <AddMeasurementButton onClick={handleAddMeasurement}>
          {t("patientPage_measurements_add")}
        </AddMeasurementButton>

        <PinnedTile>
          {isFetching ? (
            <ModalSkeleton isPinnedGraph />
          ) : (
            <>
              {pinnedMeasurementContent}
              {selectedSpirometryMeasurementValue?.spirometryTestIds &&
                !hideSpirometrySection && (
                  <SpirometrySection
                    selectedMeasurement={selectedSpirometryMeasurementValue}
                    isPinnedGraph
                    measurementType={measurementType}
                    unit={config?.unit}
                    patientId={patient.id.toString()}
                  />
                )}

              {isFitbitSleep &&
                selectedSleepSession &&
                !hideSpirometrySection && (
                  <FitbitSleepSelectedSession session={selectedSleepSession} />
                )}

              {isTreatmentOpen && treatment && (
                <TreatmentSection treatment={treatment as NebulizerChartRow} />
              )}

              <OptionsContainer>
                <DotsContainer
                  onClick={() => setAreOptionsOpen((prevState) => !prevState)}
                  ref={optionsButtonRef}
                >
                  <HorizontalDots />
                </DotsContainer>

                {areOptionsOpen ? (
                  <PinnedToogleContainer ref={optionsWrapperRef}>
                    <span>{t("patientPage_pinGraph")}</span>
                    <Toggle
                      checked={isPinned}
                      onCheckedChange={handlePinnedGraphChange}
                    />
                  </PinnedToogleContainer>
                ) : null}
              </OptionsContainer>
            </>
          )}
        </PinnedTile>
      </ChartsContainer>
    </>
  );
}
